// Reference boardwide. These hit the whole board, or the rules. Most have no aim.
// One per hook: a trade of squares in steps, terrain in a region, a row, and the roll.

import {
  BOARD_SIZE,
  CLONE_PLIES,
  CLONE_SEED_RANGE,
  DEADLY_PLIES,
  EXTRA_ROLLS,
  EXTRA_ROLLS_EARLIEST_TURN,
  FORCED_PEACE_PLIES,
  REGION_SIDE,
  TRADE_PIECES,
  TRADE_PIECES_MIN,
} from '../../constants';
import { promoteIfDue } from '../../engine/apply';
import { ALL_SQUARES, rankOf, squareIndex, squareName } from '../../engine/squares';
import { isSolid, withTerrain } from '../../engine/terrain';
import type { Board, Side } from '../../engine/types';
import type { CastStep, GameState, PieceMove } from '../../game/state';
import type { Rng } from '../../lib/rng';
import {
  addLog,
  defineMechanic,
  describePiece,
  lineFor,
  noEffect,
  pieceAt,
  say,
  settleLanding,
  squaresWhere,
  stopMoved,
  valueAt,
} from '../helpers';
import { reachLead } from '../reach';
import { openRegion } from '../region';
import type { SpellMechanic } from '../types';

const SIDES: readonly Side[] = ['gold', 'violet'];

/**
 * The pieces of one side that trade squares, in the order they move. Each
 * goes to the square of the next, and the last goes to the square of the
 * first, so no piece keeps its square.
 */
const tradesForSide = (state: GameState, side: Side, rng: Rng): PieceMove[] => {
  const candidates = squaresWhere(
    state,
    (piece) => piece.owner === side && !piece.royal && piece.kind !== 'dormant',
  );
  const squares = rng.shuffle(candidates).slice(0, TRADE_PIECES);
  if (squares.length < TRADE_PIECES_MIN) return [];
  return squares.map((from, index) => ({
    pieceId: pieceAt(state, from).id,
    from,
    to: squares[(index + 1) % squares.length] ?? from,
  }));
};

/**
 * Seed from Garrett, 2026-10-11: "pick three random pieces from my own side
 * and swap them and same for their side, animate them 1 by 1, but each team at
 * same time". Before that, every piece that was not royal traded its abilities
 * at random: "shuffle all abilities is too extreme".
 * Hook: castSteps. The board changes at once. The narrator shows one step
 * after the other, with one piece of each side in a step.
 */
export const tradeSquares = defineMechanic({
  id: 'trade-squares',
  polarity: 'board',
  family: 'placement',
  spec: {
    summary: `${TRADE_PIECES} random pieces of each side that are not royal trade squares with each other. Each cannot move for one turn.`,
    prompt: '',
    lines: {
      cast: '{count} pieces traded squares.',
      step: '{who} goes to {square}.',
      noEffect: 'No side had two pieces to trade squares.',
    },
  },
  cast: ({ state, rng, self }) => {
    const bySide = SIDES.map((side) => tradesForSide(state, side, rng));
    const moves = bySide.flat();
    if (moves.length === 0) return noEffect(state, self);
    const arrivals = new Map(moves.map((move) => [move.to, move.from]));
    const traded: Board = state.board.map((piece, square) => {
      const from = arrivals.get(square);
      if (from === undefined) return piece;
      return promoteIfDue({ ...pieceAt(state, from), moved: true }, square);
    });
    const board = stopMoved(
      traded,
      moves.map((move) => move.to),
    );
    const stepCount = Math.max(...bySide.map((side) => side.length));
    const castSteps: CastStep[] = Array.from({ length: stepCount }, (_, index) => {
      const stepMoves = bySide.flatMap((side) => side[index] ?? []);
      const text = stepMoves
        .map((move) =>
          lineFor(self, 'step', {
            who: describePiece(pieceAt(state, move.from), move.from),
            square: squareName(move.to),
          }),
        )
        .join(' ');
      return { moves: stepMoves, text };
    });
    const said = say({ ...state, board, castSteps }, self, 'cast', { count: moves.length });
    const logged = castSteps.reduce((next, step) => addLog(next, self.polarity, step.text), said);
    return moves.reduce((next, move) => settleLanding(next, move.to), logged);
  },
});

/** The squares a pick may take for a region: every square whose region has an open square. */
const regionPicks = (state: GameState): number[] =>
  ALL_SQUARES.filter((square) => openRegion(state, square).open.length > 0);

/** The squares that can take a deadly square: empty, with no terrain. */
const openSquares = (state: GameState): number[] =>
  ALL_SQUARES.filter(
    (square) => state.board[square] === null && state.rules.terrain[square] === undefined,
  );

/**
 * Hook: terrain. The rules hold a terrain layer the search sees. applyMove
 * removes a piece that lands on a deadly square or crosses it. tickTerrain
 * counts it down in plies.
 * Changed 2026-10-15. Rob: "It's now a single targeted space, and ALSO
 * consumes any piece that crosses it in the course of its move." From
 * 2026-10-12 the caster picked a region and the square was a random open one
 * in it. The AI aims at the squares the enemy reaches sooner than it does.
 */
export const deadlySquare = defineMechanic({
  id: 'deadly-square',
  polarity: 'board',
  family: 'ground',
  targetCount: 1,
  aiScore: (state, square, caster) => -reachLead(state, [square], caster),
  spec: {
    summary: `The empty square you pick is a deadly square for ${DEADLY_PLIES / 2} turns. A piece that lands there or crosses it is gone.`,
    prompt: 'Pick an empty square for the deadly square.',
    lines: {
      cast: '{square} is deadly square for {turns} turns.',
      noEffect: 'No empty square to burn.',
    },
  },
  eligible: (state) => openSquares(state),
  cast: ({ state, self }, [square]) => {
    if (square === undefined) return state;
    if (!openSquares(state).includes(square)) return noEffect(state, self);
    const rules = withTerrain(state.rules, [square], { kind: 'deadly', plies: DEADLY_PLIES });
    return say({ ...state, rules }, self, 'cast', {
      square: squareName(square),
      turns: DEADLY_PLIES / 2,
    });
  },
});

/**
 * Seed from Garrett: a square that clones the piece that lands in it.
 * Hook: terrain. applyMove puts a copy of the piece on an empty neighbor of
 * the clone square. The seed on the terrain picks the neighbor, so the search
 * and the game agree. The flow gives the copy its id.
 * Until 2026-10-12 the square was a random one of the squares that both
 * sides reach in the same number of moves. Now the caster picks a region, as
 * for the deadly square, and the square is a random open one in it. The AI
 * aims at the squares it reaches sooner than the enemy does.
 */
export const cloneSquare = defineMechanic({
  id: 'clone-square',
  polarity: 'board',
  family: 'ground',
  targetCount: 1,
  region: (state, _caster, square) => openRegion(state, square),
  aiScore: (state, square, caster) => reachLead(state, openRegion(state, square).open, caster),
  spec: {
    summary: `A random empty square in the ${REGION_SIDE} by ${REGION_SIDE} region you pick is a clone square for ${CLONE_PLIES / 2} turns. A piece that lands there gets a copy on a random empty square beside it.`,
    prompt: `Pick a ${REGION_SIDE} by ${REGION_SIDE} region for the clone square.`,
    lines: {
      cast: '{square} is a clone square for {turns} turns.',
      noEffect: 'No empty square for a clone square.',
    },
  },
  eligible: (state) => regionPicks(state),
  cast: ({ state, rng, self }, [picked]) => {
    if (picked === undefined) return state;
    const { open } = openRegion(state, picked);
    if (open.length === 0) return noEffect(state, self);
    const square = rng.pick(open);
    const rules = withTerrain(state.rules, [square], {
      kind: 'clone',
      plies: CLONE_PLIES,
      seed: rng.int(CLONE_SEED_RANGE),
    });
    return say({ ...state, rules }, self, 'cast', {
      square: squareName(square),
      turns: CLONE_PLIES / 2,
    });
  },
});

/** The squares of a rank that can hold a piece, from the left. A solid square is not one. */
const rowSquares = (state: GameState, rank: number): number[] =>
  Array.from({ length: BOARD_SIZE }, (_, file) => squareIndex(file, rank)).filter(
    (square) => !isSolid(state.rules, square),
  );

/** A row with a piece on it and a second square for that piece to go to. */
const canShuffleRow = (state: GameState, rank: number): boolean => {
  const squares = rowSquares(state, rank);
  return squares.length > 1 && squares.some((square) => state.board[square] !== null);
};

/** The tries for an order that moves a piece. A shuffle can leave every piece where it was. */
const ROW_SHUFFLE_TRIES = 8;

/**
 * For each square of the row, the square its new content comes from. Some
 * piece moves, unless every try leaves the row as it was.
 */
const shuffledRow = (state: GameState, squares: readonly number[], rng: Rng): number[] => {
  let order = [...squares];
  for (let attempt = 0; attempt < ROW_SHUFFLE_TRIES; attempt += 1) {
    order = rng.shuffle(squares);
    const changes = squares.some((square, index) => {
      const from = order[index] ?? square;
      return state.board[from] !== state.board[square];
    });
    if (changes) break;
  }
  return order;
};

/**
 * What the caster gains when a row is shuffled: the enemy pieces lose their
 * places, and its own do too, and its own cannot move for the rest of the
 * turn. So a row is worth the enemy pieces on it, less its own.
 */
const rowScore = (state: GameState, rank: number, caster: Side): number =>
  rowSquares(state, rank).reduce((sum, square) => {
    const piece = state.board[square];
    if (!piece || piece.owner === 'neutral') return sum;
    return sum + (piece.owner === caster ? -1 : 1) * valueAt(state, square);
  }, 0);

/**
 * Seed from Rob: "Shuffle any row on the board".
 * Hook: relocation of many pieces, with one pick. The caster picks a square,
 * and the pieces of its rank go to random squares of that rank: the pieces of
 * both sides, a royal piece too, and the empty squares take part. A solid
 * square stays out. A piece that lands on a deadly square is gone, and one on
 * a clone square gets a copy. The stop after a cast holds each piece of the
 * caster that moved, for the rest of the turn.
 */
export const rowScramble = defineMechanic({
  id: 'row-scramble',
  polarity: 'board',
  family: 'placement',
  targetCount: 1,
  aiScore: (state, square, caster) => rowScore(state, rankOf(square), caster),
  spec: {
    summary:
      'The pieces in the row you pick, of both sides, go to random squares of that row. A deadly square removes a piece that lands on it.',
    prompt: 'Pick a square in the row to shuffle.',
    lines: { cast: 'The pieces on row {row} go to random squares of the row.' },
  },
  eligible: (state) =>
    Array.from({ length: BOARD_SIZE }, (_, rank) => rank)
      .filter((rank) => canShuffleRow(state, rank))
      .flatMap((rank) => rowSquares(state, rank)),
  cast: ({ state, rng, self }, [picked]) => {
    if (picked === undefined) return state;
    const rank = rankOf(picked);
    const squares = rowSquares(state, rank);
    const order = shuffledRow(state, squares, rng);
    const board = [...state.board];
    const arrivals: number[] = [];
    squares.forEach((square, index) => {
      const from = order[index] ?? square;
      const piece = state.board[from] ?? null;
      if (from === square || piece === null) {
        board[square] = piece;
        return;
      }
      board[square] = { ...piece, moved: true };
      arrivals.push(square);
    });
    const said = say({ ...state, board }, self, 'cast', { row: rank + 1 });
    return arrivals.reduce((next, square) => settleLanding(next, square), said);
  },
});

/**
 * Seed from Rob: "Nobody may capture any pieces for a turn".
 * Hook: a timed rule in `rules`. `moves.ts` offers no capture while it is
 * on, so the search sees it. It covers the turn of the cast and the next
 * turn of the other side. A push, a deadly square, and a blast still remove
 * a piece.
 */
export const forcedPeace = defineMechanic({
  id: 'forced-peace',
  polarity: 'board',
  family: 'timer',
  spec: {
    summary:
      'No piece can capture on this turn and on the next turn of the enemy. A push, a deadly square, and a blast still remove a piece.',
    prompt: '',
    lines: { cast: 'No piece can capture on this turn and on the next.' },
  },
  cast: ({ state, self }) =>
    say({ ...state, rules: { ...state.rules, noCapturePlies: FORCED_PEACE_PLIES } }, self, 'cast'),
});

/** Hook: the roll. pendingRolls above zero sends the caster back to the die. */
export const extraRolls = defineMechanic({
  id: 'extra-rolls',
  polarity: 'board',
  family: 'timer',
  earliestTurn: EXTRA_ROLLS_EARLIEST_TURN,
  spec: {
    summary: `Cast ${EXTRA_ROLLS} more times.`,
    prompt: '',
    lines: { cast: 'Cast {count} more times.' },
  },
  cast: ({ state, self }) =>
    say({ ...state, pendingRolls: state.pendingRolls + EXTRA_ROLLS }, self, 'cast', {
      count: EXTRA_ROLLS,
    }),
});

export const BOARDWIDE: readonly SpellMechanic[] = [
  tradeSquares,
  deadlySquare,
  cloneSquare,
  rowScramble,
  forcedPeace,
  extraRolls,
];
